import { Radio } from 'lucide-react';

/**
 * Compact live-score chip for in-play games. Renders inline next to team
 * names, mirroring the visual weight of the final-score text so live and
 * finalised games sit in the same row slot rather than pushing the team
 * names off screen with a large red banner.
 *
 * Renders nothing for pre-match (no scores yet) or finalised (is_completed).
 *
 * Props:
 *   match — { home_score, away_score, is_completed, status }
 *
 * Status formats we handle:
 *   EPL (TheSportsDB):  "1H" / "2H" / "HT" / "ET" / "P" / "67'"
 *   AFL (afl.com.au):   "LIVE" / "IN_PROGRESS"
 *   NRL:                "1H" / "2H" / "HT"
 *   "NS" / null — pre-match (nothing to show)
 *   "FT" / "FULL_TIME" / "AET" / "PEN" — final (parent renders score, not this badge)
 */
export default function InPlayBadge({ match }) {
  if (!match) return null;
  if (match.is_completed) return null;
  const hs = match.home_score;
  const as_ = match.away_score;
  if (hs == null || as_ == null) return null;
  const status = (match.status || '').toUpperCase();
  if (!status || status === 'NS' || status === 'FULL_TIME' || status === 'FT') return null;

  // Compact phase suffix — one or two chars where possible.
  let phase = '';
  if (status === '1H' || status === 'FIRST_HALF' || status === 'FIRSTHALF') phase = '1H';
  else if (status === '2H' || status === 'SECOND_HALF' || status === 'SECONDHALF') phase = '2H';
  else if (status === 'HT' || status === 'HALFTIME' || status === 'HALF_TIME') phase = 'HT';
  else if (status === 'ET' || status === 'EXTRA_TIME') phase = 'ET';
  else if (status === 'P') phase = 'P';
  else if (status === 'LIVE' || status === 'IN_PROGRESS' || status === 'INPROGRESS') phase = 'LIVE';
  else phase = status;

  const scoreText = `${hs}–${as_}`;
  return (
    <span
      className="inline-flex items-center gap-1 ml-auto flex-shrink-0 text-xs font-mono text-gray-600"
      title={`Live · ${scoreText} · ${phase}`}
      data-testid="in-play-badge"
    >
      <Radio className="w-2.5 h-2.5 text-[#FF3B30] animate-pulse" strokeWidth={3} />
      <span>{scoreText}</span>
      <span className="text-[10px] uppercase tracking-wider text-[#FF3B30] font-bold">{phase}</span>
    </span>
  );
}
